import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo, useRef } from 'react';
import { RiDragMoveLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './index.module.scss';

import { FunctionButton } from '@/components/ui/kit/Button';
import ProgressBar from '@/components/ui/ProgressBar';
import useCameraCommand from '@/hooks/command/mavlink/useCameraCommand';
import useParamCommand from '@/hooks/command/mavlink/useParamCommand';
import useMavLinkAddress from '@/hooks/useMavLinkAddress';

const cx = classNames.bind(styles);

// Slider 고정 크기 (픽셀)
const SLIDER_HEIGHT = 100;
const SLIDER_WIDTH = 144;

const Panel = ({ data: robots }) => {
  const robotParams = useSelector((state) => state.robotParams[robots[0].id]);
  const [params, setParams] = useState({
    MNT1_PITCH_MAX: null,
    MNT1_PITCH_MIN: null,
    MNT1_YAW_MAX: null,
    MNT1_YAW_MIN: null,
  });
  const [values, setValues] = useState({ pitch: 0, yaw: 0 });
  const attitude = useRef(values);
  const intervalId = useRef();

  const { getAddress } = useMavLinkAddress();
  const cameraCommand = useCameraCommand();
  const paramCommand = useParamCommand();

  // 수신된 파라미터 갯수
  const isLoadedCount = useMemo(() => {
    return Object.values(params).filter((value) => value !== null).length;
  }, [params]);

  // 파라미터 수신 중 여부
  const isLoading = useMemo(() => {
    return isLoadedCount < Object.keys(params).length;
  }, [isLoadedCount]);

  const [pitchMax, pitchMin, yawMax, yawMin] = useMemo(() => {
    return [
      params['MNT1_PITCH_MAX']?.value ?? 0,
      params['MNT1_PITCH_MIN']?.value ?? 0,
      params['MNT1_YAW_MAX']?.value ?? 0,
      params['MNT1_YAW_MIN']?.value ?? 0,
    ];
  }, [params]);

  // Pitch label 수직 위치
  const labelTop = useMemo(() => {
    const pitchRange = pitchMax + Math.abs(pitchMin);
    return `${(1 - (values.pitch + Math.abs(pitchMin)) / pitchRange) * 100}%`;
  }, [pitchMax, pitchMin, values.pitch]);

  // Yaw label 수평 위치
  const labelLeft = useMemo(() => {
    const yawRange = yawMax + Math.abs(yawMin);
    return `${((values.yaw + Math.abs(yawMin)) / yawRange) * 100}%`;
  }, [yawMax, yawMin, values.yaw]);

  useMountEffect(() => {
    const address = getAddress(robots[0].id);
    const paramsGroup = Object.keys(params).map((key) => [...address, key, -1]);

    paramCommand.read(robots[0], paramsGroup);
  });

  useUpdateEffect(() => {
    const { lastParamId } = robotParams;
    if (!Object.keys(params).includes(lastParamId)) return;

    setParams((prev) => ({
      ...prev,
      [lastParamId]: {
        value: robotParams.params[lastParamId].value,
        type: robotParams.params[lastParamId].type, // TODO: 필요성 검토
      },
    }));
  }, [robotParams]);

  useUpdateEffect(() => {
    attitude.current = values;
  }, [values]);

  const handleChange = (e) => {
    setValues({
      ...values,
      [e.target.name]: Number(e.target.value),
    });
  };

  const startDrag = () => {
    intervalId.current = setInterval(() => {
      const { pitch, yaw } = attitude.current;
      cameraCommand.gimbalMove(robots[0], pitch, yaw);
    }, 100);
  };

  const stopDrag = () => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }
  };

  const doReset = () => {
    setValues({ pitch: 0, yaw: 0 });
    cameraCommand.gimbalReset(robots[0]);
  };

  // '+' 기호 추가
  const withSign = (number) => {
    if (number > 0) return `+${number}`;
    return number;
  };

  return (
    <div className={cx('container')}>
      {isLoading && <ProgressBar current={isLoadedCount} total={Object.keys(params).length} />}
      {!isLoading && (
        <>
          <div className={cx('sliderWrapper')}>
            <input
              name="pitch"
              type="range"
              min={params['MNT1_PITCH_MIN'].value}
              max={params['MNT1_PITCH_MAX'].value}
              value={values.pitch}
              onChange={handleChange}
              onMouseDown={startDrag}
              onMouseUp={stopDrag}
              className={cx(['slider', 'pitch'])}
              style={{ height: SLIDER_HEIGHT }}
            />
            <div className={cx(['labelRange', 'pitch'])}>
              <div className={cx(['label', 'pitch'])} style={{ top: labelTop }}>
                {withSign(values.pitch)}
              </div>
            </div>
          </div>
          <div className={cx('sliderWrapper')}>
            <input
              name="yaw"
              type="range"
              min={params['MNT1_YAW_MIN'].value}
              max={params['MNT1_YAW_MAX'].value}
              value={values.yaw}
              onChange={handleChange}
              onMouseDown={startDrag}
              onMouseUp={stopDrag}
              className={cx(['slider', 'yaw'])}
              style={{ width: SLIDER_WIDTH }}
            />
            <div className={cx(['labelRange', 'yaw'])}>
              <div className={cx(['label', 'yaw'])} style={{ left: labelLeft }}>
                {withSign(values.yaw)}
              </div>
            </div>
          </div>
          <FunctionButton text="Center All Axes" type="small" center onClick={doReset} className={cx('button')} />
        </>
      )}
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Gimbal';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiDragMoveLine;
Command.Panel = Panel;

export default Command;
